import { kindShortLabel } from '../../lib/phaseKinds';
import React, { useState, useMemo } from 'react';
import { format, addDays, parseISO, isToday } from 'date-fns';
import { useStore } from '../../store/StoreContext';
import { HelpTip, Button, IconButton } from '../ui';
import JobModal from '../modals/JobModal';
import { formatTime12 } from '../../utils/formatters';
import { useToast } from '../../hooks/useToast';

// ===== DISPATCH — RUN SHEET (2026-08-03) =====
// The day as a flat, PRINTABLE list — essentially the Daily Dispatch SMS
// content as an office page. Grouped by crew lead (day_sequence order
// inside a group), walkthroughs at the bottom, one Print button. The
// print CSS uses the visibility trick so only the sheet itself lands on
// paper — no sidebar, no toolbar, no tabs.

// Phase labels generated from segments (phaseKinds.js, 2026-08-26).
const STATUS_PILL = {
    unassigned: 'bg-amber-100 text-amber-800',
    assigned: 'bg-blue-100 text-blue-800',
    dispatched: 'bg-emerald-100 text-emerald-800',
    in_progress: 'bg-sky-100 text-sky-800',
    completed: 'bg-slate-100 text-slate-500',
};

const shortAddr = (a) => a ? a.split(',').slice(0, 2).map(s => s.trim()).join(', ') : '';

export default function DispatchRunSheet({ viewSwitcher }) {
    const { jobs, clients, crew, trucks, saveJob, removeJob } = useStore();
    const { show: showToast } = useToast();
    const [dateIso, setDateIso] = useState(() => format(new Date(), 'yyyy-MM-dd'));
    const [editJob, setEditJob] = useState(null);

    const day = parseISO(dateIso);
    const getClient = (id) => clients.find(c => c.id === id);
    const getCrew = (id) => crew.find(c => c.id === id);
    const getTruck = (id) => trucks.find(t => t.id === id);

    const { groups, walkthroughs } = useMemo(() => {
        const moves = [], walkthroughs = [];
        for (const j of (jobs || [])) {
            if (j._isSpanDay || j.status === 'cancelled' || !j.move_date) continue;
            const dur = Math.max(1, j.duration_days || 1);
            const covers = j.move_date === dateIso
                || (dur > 1 && j.move_date < dateIso && dateIso <= format(addDays(parseISO(j.move_date), dur - 1), 'yyyy-MM-dd'));
            if (!covers) continue;
            if (j.type === 'WALKTHROUGH') walkthroughs.push(j);
            else moves.push(j);
        }
        const byLead = new Map();
        for (const j of moves) {
            const key = j.leader_id || '_unassigned';
            if (!byLead.has(key)) byLead.set(key, []);
            byLead.get(key).push(j);
        }
        const groups = [];
        for (const [key, list] of byLead) {
            list.sort((a, b) => (a.day_sequence || 1) - (b.day_sequence || 1) || String(a.time || '99').localeCompare(String(b.time || '99')));
            const firstTime = list.reduce((m, j) => (j.time && (!m || j.time < m)) ? j.time : m, null);
            groups.push({ key, lead: key === '_unassigned' ? null : getCrew(key), jobs: list, firstTime });
        }
        // Leads in start-time order; Unassigned always last.
        groups.sort((a, b) => (a.key === '_unassigned') - (b.key === '_unassigned')
            || String(a.firstTime || '99').localeCompare(String(b.firstTime || '99')));
        walkthroughs.sort((a, b) => String(a.time || '99').localeCompare(String(b.time || '99')));
        return { groups, walkthroughs };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs, dateIso, crew, clients]);

    const handleSave = async (form, deleteId) => {
        if (deleteId) { await removeJob(deleteId); showToast('Job deleted'); return; }
        await saveJob(form);
        showToast('Job updated');
    };

    const jobCount = groups.reduce((n, g) => n + g.jobs.length, 0);

    return (
        <div className="p-6">
            {/* Print CSS — only #bbm-run-sheet reaches paper. */}
            <style>{`@media print {
                body * { visibility: hidden !important; }
                #bbm-run-sheet, #bbm-run-sheet * { visibility: visible !important; }
                #bbm-run-sheet { position: absolute !important; left: 0; top: 0; width: 100%; padding: 0 !important; border: none !important; box-shadow: none !important; }
                #bbm-run-sheet .no-print { display: none !important; }
            }`}</style>

            {/* Header (screen only) */}
            <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
                    <div className="flex items-center gap-2.5">
                        <h1 className="font-bold font-lemonmilk text-slate-900 text-2xl">Run Sheet</h1>
                        <HelpTip title="How the run sheet works">
                            <p>The day as a flat list — every job grouped under its <span className="font-semibold">crew lead</span> in run order, with time, addresses, crew, trucks, dispatcher notes, and status. Walkthroughs list at the bottom; jobs with no crew sit under "Unassigned."</p>
                            <p><span className="font-semibold">Print</span> puts just the sheet on paper (no app chrome) — built for the counter. Tap any row on screen to open the job editor.</p>
                        </HelpTip>
                    </div>
                    {viewSwitcher}
                </div>
                <div className="flex items-center gap-1">
                    <IconButton label="Previous day" size="sm" onClick={() => setDateIso(format(addDays(day, -1), 'yyyy-MM-dd'))}>←</IconButton>
                    <Button variant={isToday(day) ? 'primary' : 'secondary'} size="sm" onClick={() => setDateIso(format(new Date(), 'yyyy-MM-dd'))}>Today</Button>
                    <IconButton label="Next day" size="sm" onClick={() => setDateIso(format(addDays(day, 1), 'yyyy-MM-dd'))}>→</IconButton>
                    <Button variant="secondary" size="sm" className="ml-2" onClick={() => window.print()}>🖨 Print</Button>
                </div>
            </div>

            <div id="bbm-run-sheet" className="bg-white border border-slate-200 rounded-xl p-6 max-w-4xl">
                {/* Sheet header (prints) */}
                <div className="flex items-baseline justify-between border-b-2 border-slate-900 pb-2 mb-4">
                    <div className="font-bold font-lemonmilk text-lg text-slate-900">BEATY BROTHERS — DAILY RUN SHEET</div>
                    <div className="font-bold text-sm text-slate-700">{format(day, 'EEEE, MMMM d, yyyy')}</div>
                </div>

                {jobCount === 0 && walkthroughs.length === 0 && (
                    <div className="py-10 text-center text-slate-400">Nothing scheduled for {format(day, 'EEE, MMM d')}.</div>
                )}

                {groups.map(g => (
                    <div key={g.key} className="mb-5" style={{ breakInside: 'avoid' }}>
                        <div className={`flex items-center gap-2 border-b border-slate-300 pb-1 mb-2 ${g.lead ? '' : 'text-amber-700'}`}>
                            {g.lead && <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: g.lead.display_color || '#64748b' }}></span>}
                            <span className="font-extrabold text-[15px]">{g.lead ? g.lead.name : '⚠ Unassigned'}</span>
                            <span className="text-xs text-slate-400">{g.jobs.length} job{g.jobs.length !== 1 ? 's' : ''}</span>
                        </div>
                        {g.jobs.map(j => {
                            const client = getClient(j.client_id);
                            const members = (j.crew_ids || []).filter(id => id !== j.leader_id).map(id => getCrew(id)?.name).filter(Boolean);
                            const truckNames = (j.truck_ids || []).map(id => getTruck(id)?.name).filter(Boolean);
                            const midSpan = j.move_date !== dateIso;
                            return (
                                <div key={j.id} className="flex gap-3 py-2 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1" style={{ breakInside: 'avoid' }} onClick={() => setEditJob(j)}>
                                    <div className="w-20 flex-shrink-0 text-sm font-bold text-slate-900">
                                        {midSpan ? `Day ${Math.round((parseISO(dateIso) - parseISO(j.move_date)) / 86400000) + 1}/${j.duration_days}` : (formatTime12(j.time) || 'No time')}
                                        {!midSpan && j.duration ? <div className="text-[10px] font-medium text-slate-400">{j.duration}h est</div> : null}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-sm text-slate-900">{client?.name || 'Unknown'}</span>
                                            <span className="text-[11px] text-slate-500">{(j.type || '').replace(/_/g, ' ')}</span>
                                            {j.phase_kind && <span className="text-[10px] font-extrabold text-slate-600 border border-slate-300 rounded px-1">{kindShortLabel(j.phase_kind)}</span>}
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${STATUS_PILL[j.status] || 'bg-slate-100 text-slate-600'}`}>{(j.status || '').replace(/_/g, ' ')}</span>
                                        </div>
                                        {(j.from_address || j.to_address) && (
                                            <div className="text-xs text-slate-600 mt-0.5">
                                                {shortAddr(j.from_address) || '—'} <span className="text-slate-400">→</span> {shortAddr(j.to_address) || '—'}
                                            </div>
                                        )}
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {members.length > 0 && <span>Crew: {members.join(', ')}</span>}
                                            {members.length > 0 && truckNames.length > 0 && <span> · </span>}
                                            {truckNames.length > 0 && <span>Trucks: {truckNames.join(' + ')}</span>}
                                            {members.length === 0 && truckNames.length === 0 && <span className="text-amber-600 font-semibold">No crew or trucks assigned</span>}
                                        </div>
                                        {(j.dispatcher_notes || j.special_notes || j.heavy_items) && (
                                            <div className="text-xs text-slate-500 italic mt-0.5">
                                                {[j.dispatcher_notes, j.special_notes, j.heavy_items && `Heavy: ${j.heavy_items}`].filter(Boolean).join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}

                {walkthroughs.length > 0 && (
                    <div className="mt-4" style={{ breakInside: 'avoid' }}>
                        <div className="font-extrabold text-[13px] text-slate-500 uppercase tracking-wider border-b border-slate-300 pb-1 mb-2">Walkthroughs</div>
                        {walkthroughs.map(j => {
                            const client = getClient(j.client_id);
                            const estimator = getCrew(j.leader_id);
                            return (
                                <div key={j.id} className="flex gap-3 py-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1" onClick={() => setEditJob(j)}>
                                    <div className="w-20 flex-shrink-0 text-sm font-bold text-slate-900">{formatTime12(j.time) || 'No time'}</div>
                                    <div className="flex-1 text-sm text-slate-700">
                                        <span className="font-semibold">{client?.name || 'Unknown'}</span>
                                        {estimator && <span className="text-slate-500"> — {estimator.name}</span>}
                                        {j.from_address && <span className="text-xs text-slate-500 block">{shortAddr(j.from_address)}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {editJob && (
                <JobModal
                    job={editJob}
                    onClose={() => setEditJob(null)}
                    onSave={async (form, deleteId) => { await handleSave(form, deleteId); setEditJob(null); }}
                    clients={clients} crew={crew} trucks={trucks} jobs={jobs}
                />
            )}
        </div>
    );
}
